import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/Common/StatusBadge';
import { formatDate, formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';
import ViewTranscript from './CaseDetail/ViewTranscript';
import EditTranscript from './CaseDetail/EditTranscript';
import StatusUpdate from './CaseDetail/StatusUpdate';
import Followups from './CaseDetail/Followups';
import Tickets from './CaseDetail/Tickets';
import Documents from './CaseDetail/Documents';
import './CaseDetail.css';

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('view');

  useEffect(() => {
    fetchCaseDetail();
  }, [id]);

  const fetchCaseDetail = async () => {
    try {
      setLoading(true);
      const data = await api.cases.get(id);
      const caseResult = data.case || data.data || data;
      setCaseData(data);
    } catch (error) {
      toast.error('Failed to load case details');
      console.error(error);
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this case? This action cannot be undone.')) return;
    try {
      await api.cases.delete(id);
      toast.success('Case deleted successfully');
      navigate('/cases');
    } catch (error) {
      toast.error('Failed to delete case');
    }
  };

  const handleCaseUpdate = () => {
    fetchCaseDetail();
    toast.success('Case updated successfully');
  };

  if (loading) {
    return (
      <div className="case-detail-page">
        <div className="skeleton" style={{ height: '200px' }}></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="case-detail-page">
        <p className="text-gray">Case not found</p>
      </div>
    );
  }

  return (
    <div className="case-detail-page">
      <div className="case-detail-header">
        <button className="back-button" onClick={() => navigate('/cases')}>
          <ArrowLeft size={20} />
          Back to Cases
        </button>
        <div className="case-header-info">
          <h1>{caseData.student_name || caseData.full_name || 'Case Details'}</h1>
          <div className="case-meta">
            {caseData.case_number && <span>Case #{caseData.case_number}</span>}
              {isAdmin && (
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}
                >
                  <Trash2 size={16} /> Delete Case
                </button>
              )}
            {caseData.status && <StatusBadge status={caseData.status} />}
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'view' ? 'active' : ''}`}
            onClick={() => setActiveTab('view')}
          >
            View Transcript
          </button>
          {isAdmin && (
            <button
              className={`tab ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => setActiveTab('edit')}
            >
              Edit Transcript
            </button>
          )}
          <button
            className={`tab ${activeTab === 'status' ? 'active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            Status Update
          </button>
          <button
            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
          <button
            className={`tab ${activeTab === 'followups' ? 'active' : ''}`}
            onClick={() => setActiveTab('followups')}
          >
            Followups
          </button>
          <button
            className={`tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            Tickets (Communication)
          </button>
        </div>
      </div>

      <div className="tabs-content">
        {activeTab === 'view' && (
          <ViewTranscript caseData={caseData} />
        )}
        {activeTab === 'edit' && isAdmin && (
          <EditTranscript
            caseData={caseData}
            onUpdate={handleCaseUpdate}
          />
        )}
        {activeTab === 'status' && (
          <StatusUpdate
            caseData={caseData}
            onUpdate={handleCaseUpdate}
            isAdmin={isAdmin}
          />
        )}
        {activeTab === 'documents' && (
          <Documents caseId={id} isAdmin={isAdmin} />
        )}
        {activeTab === 'followups' && (
          <Followups caseId={id} />
        )}
        {activeTab === 'tickets' && (
          <Tickets caseId={id} currentUser={user} />
        )}
      </div>
    </div>
  );
}
